/**
 * Aspect — PlanAnalyzer (browser -> HTTP orchestrator)
 * --------------------------------------------------------------
 * Uploads a 2D render / floor plan to the orchestrator's `/analyze-plan`
 * endpoint. The server runs a Claude-vision pass to estimate the apartment's
 * dimensions and openings, rebuilds the room, and pushes the new structure over
 * the WS bridge (the viewport rebuilds live). This module streams the analysis
 * back as `ai:analysis` app events for the bottom-left panel. No DOM access here.
 */

import { getAiProvider } from "./ai_provider.js";
import { clearSavedPlan } from "./plan_store.js";
import { NET_STATE } from "./network.js";
import { compressImageDataUrl } from "./image_compress.js";
import { orchUrl } from "./endpoints.js";
import { orchUserError } from "./restyle_intent.js";

export const ORCH_URL = orchUrl();
const ANALYZE_TIMEOUT_MS = 210_000;

/** Fetch the orchestrator's persisted apartment (survives page refresh / server restart). */
export async function fetchActivePlan() {
  try {
    const res = await fetch(`${ORCH_URL}/active-plan`);
    if (!res.ok) return null;
    const data = await res.json();
    return data?.spec ? data : null;
  } catch {
    return null;
  }
}

/** Fetch the authoritative 3D scene (room + placements) from the orchestrator. */
export async function fetchViewportState() {
  try {
    const res = await fetch(`${ORCH_URL}/viewport-state`, { signal: AbortSignal.timeout(12_000) });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.room ? data : null;
  } catch {
    return null;
  }
}

/** Fetch a published read-only tour snapshot by id. */
export async function fetchTour(id) {
  try {
    const res = await fetch(`${ORCH_URL}/tours/${encodeURIComponent(id)}`, { signal: AbortSignal.timeout(12_000) });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.snapshot?.room ? data : null;
  } catch {
    return null;
  }
}

/** Fetch the original uploaded 2D render (full data URL) for the plan editor. */
export async function fetchActivePlanImage() {
  try {
    const res = await fetch(`${ORCH_URL}/active-plan-image`);
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.image === "string" && data.image.startsWith("data:") ? data.image : null;
  } catch {
    return null;
  }
}

export class PlanAnalyzer {
  /** @param {import('playcanvas').AppBase} app */
  constructor(app, opts = {}) {
    this.app = app;
    this.url = opts.url ?? ORCH_URL;
    this.net = opts.net ?? null;
    this.sandbox = Boolean(opts.sandbox);
    this.busy = false;
    this._abortCtrl = null;
    this._sandboxSnapshot = false;
    this._lastSnapshot = null;
  }

  /** Analyze a 2D render/floor plan and rebuild the room from the estimate. */
  async analyze(imageDataUrl, hint = "") {
    if (!imageDataUrl) return;
    if (this.busy) {
      this.app.fire("ai:analysis", { phase: "error", text: "Analysis already in progress — please wait." });
      return;
    }

    this._sandboxSnapshot = false;
    this._lastSnapshot = null;
    clearSavedPlan();
    this.app.fire("scene:sandbox-expect", {});
    this.app.fire("ui:home-reset", { analyzing: true });
    this.app.fire("ai:analysis", { phase: "progress", text: "Preparing image…" });

    let payload = imageDataUrl;
    try {
      payload = await compressImageDataUrl(imageDataUrl);
    } catch {
      /* use original if compression fails */
    }

    return this._stream("/analyze-plan", { image: payload, hint, provider: getAiProvider() }, "Analyzing the plan…");
  }

  /** Rebuild the room from a user-edited dimension spec (no image needed). */
  async rebuild(spec) {
    if (!spec) {
      this.app.fire("ai:analysis", { phase: "error", text: "Nothing to apply — no room spec loaded." });
      return false;
    }
    if (this.busy) {
      this.app.fire("ai:analysis", {
        phase: "error",
        text: "Please wait for the current operation to finish, then try Apply again.",
      });
      return false;
    }
    return this._stream("/rebuild-room", { spec }, "Rebuilding the room…");
  }

  /**
   * Targeted structure refine: correct ONLY the selected walls/doors/windows
   * against the original 2D render. `elements` is [{ id, kind }].
   */
  async refineStructure({ instruction = "", elements = [] } = {}) {
    const els = Array.isArray(elements) ? elements : [];
    if (!els.length) {
      this.app.fire("ai:analysis", { phase: "error", text: "Select a wall, door, or window to refine first." });
      return;
    }
    return this._stream(
      "/refine-structure",
      { instruction, elements: els, provider: getAiProvider() },
      "Refining structure from the 2D render…",
    );
  }

  /**
   * Commit a hand-traced pixel-space topology graph from the 2D plan editor.
   * The server rebuilds the home from it verbatim (no AI, deterministic).
   */
  async editStructure(graph) {
    if (!graph?.nodes?.length || !graph?.edges?.length) {
      this.app.fire("ai:analysis", { phase: "error", text: "Draw at least a closed room before applying." });
      return false;
    }
    return this._stream("/sync-plan-structure", { graph }, "Rebuilding from your traced plan…");
  }

  /**
   * Live plan-editor structure sync (debounced). Does not block on analysis busy.
   * @param {object} graph
   * @param {{ quiet?: boolean }} [opts]
   */
  async syncPlanStructure(graph, opts = {}) {
    if (!graph?.nodes?.length || !graph?.edges?.length) return false;
    return this._streamLive("/sync-plan-structure", { graph }, Boolean(opts.quiet));
  }

  /** Persist door/window pose changes without rebuilding wall topology. */
  async patchOpenings(patch) {
    try {
      const res = await fetch(`${this.url}/patch-plan-openings`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (
        res.ok &&
        ((patch?.upsert?.length ?? 0) > 0 ||
          (patch?.remove?.length ?? 0) > 0 ||
          patch?.graph?.nodes?.length >= 3)
      ) {
        void this.syncViewportFromHttp();
      }
      return res.ok;
    } catch {
      return false;
    }
  }

  /** Persist wall shape changes without rebuilding the whole room. */
  async patchTopology(patch) {
    try {
      const res = await fetch(`${this.url}/patch-plan-topology`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
        signal: AbortSignal.timeout(20_000),
      });
      if (res.ok) void this.syncViewportFromHttp();
      return res.ok;
    } catch {
      return false;
    }
  }

  /**
   * Pull the authoritative room snapshot over HTTP (WS resync fallback).
   * SceneManager's version gate drops stale snapshots automatically.
   */
  async syncViewportFromHttp() {
    await new Promise((r) => setTimeout(r, 400));
    const state = await fetchViewportState();
    if (state?.room) this.app.fire("scene:http-resync", state);
  }

  /** Re-lock the global scale (meters-per-pixel) and rebuild from pixel coords. */
  async recalibrate(payload) {
    if (!payload) return;
    return this._stream("/recalibrate", payload, "Recalibrating scale…");
  }

  /** Restore the default map from server/maps/ and clear any saved custom plan. */
  async resetHome() {
    // Cancel any in-flight analysis fetch so reset is never blocked.
    if (this._abortCtrl) {
      this._abortCtrl.abort();
      this._abortCtrl = null;
    }
    this.busy = false;
    this.app.fire("ai:analysis", { phase: "done" });

    try {
      if (this.sandbox) {
        await this._resetSandboxToDefault();
      } else {
        await this._doReset();
      }
      clearSavedPlan();
      this.app.fire("ui:home-reset", {});
    } catch (err) {
      const hintMsg = /Failed to fetch|orchestrator|timed out|No agent|Reset failed/i.test(err.message)
        ? "Can't reset — start the orchestrator: `cd server && npm run orchestrator`"
        : err.message;
      this.app.fire("ui:reset-failed", { text: hintMsg });
    }
  }

  /** Production SampleTour: drop the session 2D rebuild and reload the shared default. */
  async _resetSandboxToDefault() {
    this._sandboxSnapshot = false;
    this._lastSnapshot = null;
    this.app.fire("scene:sandbox-unseal", {});
    const state = await fetchViewportState();
    if (!state?.room) throw new Error("Reset failed — default tour unavailable.");
    this.app.fire("scene:http-resync", state);
  }

  /** HTTP orchestrator owns persistence; bridge is the fallback. */
  async _doReset() {
    let httpErr = null;
    try {
      const res = await fetch(`${this.url}/reset-home`, {
        method: "POST",
        signal: AbortSignal.timeout(12_000),
      });
      if (res.ok) return;
      if (res.status === 404) {
        throw new Error("Orchestrator is outdated — stop it and restart: cd server && npm run orchestrator");
      }
      httpErr = new Error(`orchestrator ${res.status}`);
    } catch (err) {
      httpErr = err;
    }

    if (this.net?.state === NET_STATE.READY) {
      try {
        await this.net.request("viewport:reset_home", {}, 15_000);
        return;
      } catch {
        /* bridge handler missing on old agent too */
      }
    }

    throw httpErr ?? new Error("Reset failed — no agent connected.");
  }

  async _stream(path, body, startText) {
    if (this.busy) return false;
    return this._streamSse(path, body, { startText, useBusy: true });
  }

  /**
   * SSE POST for live plan-editor sync — skips the global busy gate so edits
   * can stream through while the editor stays open.
   */
  async _streamLive(path, body, quiet = false) {
    return this._streamSse(path, body, { quiet, useBusy: false });
  }

  async _streamSse(path, body, opts = {}) {
    const { startText = "", quiet = false, useBusy = true } = opts;
    if (useBusy && this.busy) return false;
    if (useBusy) this.busy = true;

    const ctrl = new AbortController();
    if (useBusy) this._abortCtrl = ctrl;
    const timer = setTimeout(() => ctrl.abort(), ANALYZE_TIMEOUT_MS);

    if (startText && !quiet) {
      this.app.fire("ai:analysis", { phase: "start", text: startText });
    }

    let ok = true;
    try {
      let res = null;
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          res = await fetch(`${this.url}${path}`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            credentials: "include",
            body: JSON.stringify(body),
            signal: ctrl.signal,
          });
          break;
        } catch (err) {
          const transient = err.name === "TypeError" || /Failed to fetch|NetworkError/i.test(err.message ?? "");
          if (!transient || ctrl.signal.aborted || attempt === 3) throw err;
          if (!quiet) {
            this.app.fire("ai:analysis", { phase: "progress", text: "Waiting for the orchestrator…" });
          }
          await new Promise((r) => setTimeout(r, 800));
        }
      }
      if (!res || !res.ok || !res.body) throw new Error(`orchestrator ${res?.status ?? "unreachable"}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let sep;
        while ((sep = buf.indexOf("\n\n")) >= 0) {
          const raw = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          if (!this._handleSse(raw, { quiet })) ok = false;
        }
      }
    } catch (err) {
      ok = false;
      if (err.name === "AbortError" && this._abortCtrl !== ctrl) return false;
      if (!quiet) {
        let hintMsg = err.message;
        if (err.name === "AbortError") {
          hintMsg =
            "Analysis timed out. Check that `npm run orchestrator` is running, or try Demo mode for an instant template.";
        } else if (/Failed to fetch|orchestrator|network/i.test(err.message)) {
          hintMsg = orchUserError(err, Number(/orchestrator (\d+)/.exec(err.message)?.[1] || 0));
        }
        this.app.fire("ai:analysis", { phase: "error", text: hintMsg });
      }
    } finally {
      clearTimeout(timer);
      if (useBusy && this._abortCtrl === ctrl) this._abortCtrl = null;
      if (useBusy) this.busy = false;
      if (!quiet) this.app.fire("ai:analysis", { phase: "done" });
    }
    return ok;
  }

  _handle(raw) {
    this._handleSse(raw, { quiet: false });
  }

  /**
   * Apply room + placements over HTTP when the WS resync was missed or delayed.
   * Safe to fire unconditionally: SceneManager's snapshot gate drops it as a
   * no-op when the WS resync (same version) already applied.
   */
  async _syncViewportFromHttp() {
    return this.syncViewportFromHttp();
  }

  /** @returns {boolean} false when the stream reported an error event */
  _handleSse(raw, { quiet = false } = {}) {
    let event = "message";
    let data = "";
    for (const line of raw.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data += line.slice(5).trim();
    }
    let payload = {};
    try {
      payload = data ? JSON.parse(data) : {};
    } catch {
      /* ignore */
    }

    switch (event) {
      case "info":
        if (!quiet) this.app.fire("ai:analysis", { phase: "progress", text: payload.message ?? "" });
        break;
      case "snapshot":
        this._sandboxSnapshot = Boolean(payload?.sandbox);
        if (payload?.room) this._lastSnapshot = payload;
        this.app.fire("scene:http-resync", payload);
        break;
      case "analysis":
        if (!quiet) this.app.fire("ai:analysis", { phase: "result", result: payload });
        if (this._lastSnapshot?.room) {
          this.app.fire("scene:http-resync", this._lastSnapshot);
        } else if (!this._sandboxSnapshot) {
          this.net?.notify("viewport:request_resync", {});
          void this._syncViewportFromHttp();
        }
        break;
      case "error":
        if (!quiet) this.app.fire("ai:analysis", { phase: "error", text: payload.message ?? "Analysis error" });
        return false;
      default:
        break;
    }
    return true;
  }
}

export default PlanAnalyzer;
